import React, { useState } from "react";

import { BarChart3, Building2, Loader2, Stethoscope, TrendingUp, Users } from "lucide-react";

import { Button } from "@/components/ui/button";

import { EntityInfoBlock, EntityStatPill } from "@/components/companies/entity-ui";

import { formatCiroMoney } from "@/utils/ciroHelpers";

import { BarList, PaymentSplitChart, TrendLineChart } from "@/components/finance/ciroCharts";



const TREND_LABELS = { daily: "Günlük", monthly: "Aylık", yearly: "Yıllık" };

const BREAKDOWN_TABS = [

  { id: "agent", label: "Agent" },

  { id: "seller", label: "Satışçı" },

  { id: "doctor", label: "Doktor" },

];



const CiroDashboardPanel = ({ data, loading, isDark, textClass, labelClass, trendPeriod, onTrendPeriodChange }) => {

  const [breakdownTab, setBreakdownTab] = useState("agent");

  const kpis = data?.kpis || {};

  const breakdownItems = data?.breakdown?.[breakdownTab] || [];

  const paymentSplit = data?.payment_split || [

    { label: "Hastane Ödemesi", total: kpis.hospital_payments || 0 },

    { label: "Doktor Payı", total: kpis.doctor_payments || 0 },

  ];



  return (

    <div className="space-y-4">

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">

        <EntityStatPill label="Toplam Ciro" value={formatCiroMoney(kpis.total_ciro)} tone="emerald" isDark={isDark} />

        <EntityStatPill label="Hastane Ödemesi" value={formatCiroMoney(kpis.hospital_payments)} tone="sky" isDark={isDark} />

        <EntityStatPill label="Doktor Payı" value={formatCiroMoney(kpis.doctor_payments)} tone="violet" isDark={isDark} />

        <EntityStatPill label="Hasta Sayısı" value={Number(kpis.unique_patients || 0).toLocaleString("tr-TR")} tone="indigo" isDark={isDark} />

      </div>



      <p className={`text-xs ${labelClass}`}>

        {Number(kpis.total_operations || 0).toLocaleString("tr-TR")} toplam işlem

      </p>



      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

        <div className={`rounded-xl border p-4 xl:col-span-2 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-white"}`}>

          <div className="flex items-center justify-between gap-3 mb-4">

            <div className={`flex items-center gap-2 text-sm font-semibold ${textClass}`}>

              <TrendingUp className="w-4 h-4 text-blue-500" />

              Ciro Trendi

            </div>

            <div className={`inline-flex rounded-lg p-0.5 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>

              {Object.entries(TREND_LABELS).map(([id, label]) => (

                <button

                  key={id}

                  type="button"

                  onClick={() => onTrendPeriodChange(id)}

                  className={`px-3 py-1 text-xs rounded-md transition-all ${

                    trendPeriod === id

                      ? isDark

                        ? "bg-slate-700 text-white shadow-sm"

                        : "bg-white text-gray-900 shadow-sm"

                      : isDark

                        ? "text-slate-400 hover:text-slate-200"

                        : "text-gray-500 hover:text-gray-700"

                  }`}

                >

                  {label}

                </button>

              ))}

            </div>

          </div>

          {loading ? (

            <div className="flex items-center justify-center h-[220px]">

              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />

            </div>

          ) : (

            <TrendLineChart trend={data?.trend || []} period={trendPeriod} isDark={isDark} />

          )}

        </div>



        <EntityInfoBlock title="Ödeme Dağılımı" icon={Building2} tone="sky" isDark={isDark}>

          {loading ? (

            <Loader2 className="w-5 h-5 animate-spin text-sky-500" />

          ) : (

            <PaymentSplitChart items={paymentSplit} isDark={isDark} />

          )}

        </EntityInfoBlock>

      </div>



      <EntityInfoBlock title="Kırılım Analizi" icon={BarChart3} tone="indigo" isDark={isDark}>

        <div className="flex gap-1 mb-3">

          {BREAKDOWN_TABS.map((tab) => (

            <Button

              key={tab.id}

              type="button"

              size="sm"

              variant={breakdownTab === tab.id ? "default" : "outline"}

              className={`h-7 text-xs ${breakdownTab === tab.id ? "bg-indigo-600 hover:bg-indigo-700" : ""}`}

              onClick={() => setBreakdownTab(tab.id)}

            >

              {tab.label}

            </Button>

          ))}

        </div>

        {loading ? (

          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />

        ) : (

          <BarList items={breakdownItems} isDark={isDark} />

        )}

      </EntityInfoBlock>



      <div className={`grid grid-cols-3 gap-2 text-xs ${labelClass}`}>

        <div className="flex items-center gap-1.5">

          <Users className="w-3.5 h-3.5" />

          <span>{Number(kpis.unique_patients || 0).toLocaleString("tr-TR")} benzersiz hasta</span>

        </div>

        <div className="flex items-center gap-1.5">

          <Building2 className="w-3.5 h-3.5" />

          <span>{formatCiroMoney(kpis.hospital_payments)} hastane</span>

        </div>

        <div className="flex items-center gap-1.5">

          <Stethoscope className="w-3.5 h-3.5" />

          <span>{formatCiroMoney(kpis.doctor_payments)} klinik</span>

        </div>

      </div>

    </div>

  );

};



export default CiroDashboardPanel;

